import { PlacedBlock, SocketConnection, IsoPoint, BlockSystem } from '@/app/types/cad';
import { transformPoint } from '@/app/utils/transform';

/**
 * Generiert eine eindeutige ID
 */
function generateId(): string {
  return `conn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

interface SocketInfo {
  blockId: string;
  position: IsoPoint;
  socketIndex?: number; // Index in connection points array
  lineIndex?: number; // Index in lines array
  isStart?: boolean; // Für Linien: start oder end point?
  diameter?: number; // Durchmesser in mm
  dn?: number; // DN-Wert (aus Attributen)
}

/**
 * Berechnet die Distanz zwischen zwei IsoPoints
 */
function isoDistance(p1: IsoPoint, p2: IsoPoint): number {
  const du = p1.u - p2.u;
  const dv = p1.v - p2.v;
  return Math.sqrt(du * du + dv * dv);
}

/**
 * Extrahiert DN-Wert aus Block-Attributen
 */
function extractDnValue(block: PlacedBlock): number | undefined {
  const dnAttr = block.blockData.attributes?.find(attr => 
    attr.type === 'dn' || attr.name.toLowerCase().includes('dn')
  );
  
  if (dnAttr && typeof dnAttr.value === 'number') {
    return dnAttr.value;
  }
  
  if (dnAttr && typeof dnAttr.value === 'string') {
    // Versuche DN aus String zu extrahieren (z.B. "DN 50" -> 50)
    const match = dnAttr.value.match(/\d+/);
    return match ? parseInt(match[0]) : undefined;
  }
  
  return undefined;
}

/**
 * Sammelt alle Sockets (Connection Points und Linien-Endpunkte) aus allen Blöcken
 */
export function collectAllSockets(blocks: PlacedBlock[]): SocketInfo[] {
  const allSockets: SocketInfo[] = [];
  
  console.log('📊 collectAllSockets - Anzahl Blöcke:', blocks.length);
  
  blocks.forEach(block => {
    const dn = extractDnValue(block);
    
    console.log(`  📦 Block "${block.blockData.name}" (ID: ${block.id}):`);
    console.log(`    - Elements:`, block.blockData.elements.length);
    
    // Sammle Connection Points mit korrekten Indices
    let connectionPointIndex = 0;
    const connectionElements = block.blockData.elements.filter(el => el.type === 'connection');
    console.log(`    - Connection Points:`, connectionElements.length);
    
    block.blockData.elements.forEach((element) => {
      if (element.type === 'connection') {
        const transformedPos = transformPoint(element.position, block.transform);
        allSockets.push({
          blockId: block.id,
          position: transformedPos,
          socketIndex: connectionPointIndex,
          diameter: element.diameter,
          dn: dn,
        });
        connectionPointIndex++;
      }
    });
    
    // Sammle Linien-Endpunkte mit korrekten Indices
    let lineIndex = 0;
    const lineElements = block.blockData.elements.filter(el => el.type === 'line');
    console.log(`    - Line Elements:`, lineElements.length);
    
    block.blockData.elements.forEach((element) => {
      if (element.type === 'line') {
        const transformedStart = transformPoint(element.start, block.transform);
        const transformedEnd = transformPoint(element.end, block.transform);
        
        allSockets.push({
          blockId: block.id,
          position: transformedStart,
          lineIndex: lineIndex,
          isStart: true,
          diameter: element.width * 10, // Linienbreite als Durchmesser-Schätzung
          dn: dn,
        });
        
        allSockets.push({
          blockId: block.id,
          position: transformedEnd,
          lineIndex: lineIndex,
          isStart: false,
          diameter: element.width * 10,
          dn: dn,
        });
        
        lineIndex++;
      }
    });
  });
  
  console.log('📊 Gesamt gefundene Sockets:', allSockets.length);
  
  return allSockets;
}

/**
 * Gruppiert Sockets nach Position (mit Toleranz)
 * Erstellt nur Gruppen, wenn Sockets von VERSCHIEDENEN Blöcken stammen
 */
function groupSocketsByPosition(sockets: SocketInfo[], tolerance: number = 0.1): SocketInfo[][] {
  const groups: SocketInfo[][] = [];
  const used = new Set<number>();
  
  console.log(`🔍 Gruppiere ${sockets.length} Sockets mit Toleranz ${tolerance}...`);
  
  for (let i = 0; i < sockets.length; i++) {
    if (used.has(i)) continue;
    
    const group: SocketInfo[] = [sockets[i]];
    used.add(i);
    
    for (let j = i + 1; j < sockets.length; j++) {
      if (used.has(j)) continue;
      
      // WICHTIG: Sockets vom selben Block dürfen NICHT verbunden werden
      if (sockets[i].blockId === sockets[j].blockId) {
        continue;
      }
      
      const distance = isoDistance(sockets[i].position, sockets[j].position);
      
      if (distance < tolerance) {
        console.log(`  ✅ Sockets ${i} und ${j} von verschiedenen Blöcken sind nah beieinander (Distanz: ${distance.toFixed(4)})`, {
          block1: sockets[i].blockId,
          block2: sockets[j].blockId
        });
        console.log(`    Socket ${i}:`, sockets[i].position);
        console.log(`    Socket ${j}:`, sockets[j].position);
        group.push(sockets[j]);
        used.add(j);
      } else {
        // Nur ausgeben wenn relativ nah, aber nicht nah genug
        if (distance < tolerance * 10) {
          console.log(`  ❌ Sockets ${i} und ${j} sind zu weit entfernt (Distanz: ${distance.toFixed(4)} > ${tolerance})`, {
            block1: sockets[i].blockId,
            block2: sockets[j].blockId
          });
          console.log(`    Socket ${i}:`, sockets[i].position);
          console.log(`    Socket ${j}:`, sockets[j].position);
        }
      }
    }
    
    // Nur Gruppen mit mindestens 2 Sockets von VERSCHIEDENEN Blöcken sind echte Verbindungen
    const uniqueBlocks = new Set(group.map(s => s.blockId));
    if (group.length >= 2 && uniqueBlocks.size >= 2) {
      console.log(`  🎉 Gültige Verbindung mit ${group.length} Sockets von ${uniqueBlocks.size} verschiedenen Blöcken gefunden!`);
      groups.push(group);
    } else if (group.length >= 2) {
      console.log(`  ⚠️ Gruppe mit ${group.length} Sockets gefunden, aber alle vom selben Block - KEINE Verbindung`);
    } else {
      console.log(`  ⚠️ Socket ${i} ist isoliert (keine Verbindung)`);
    }
  }
  
  console.log(`🔍 Gesamt ${groups.length} GÜLTIGE Verbindungen gefunden`);
  
  return groups;
}

/**
 * Berechnet die durchschnittliche Position einer Socket-Gruppe
 */
function getAveragePosition(sockets: SocketInfo[]): IsoPoint {
  const sumU = sockets.reduce((sum, s) => sum + s.position.u, 0);
  const sumV = sockets.reduce((sum, s) => sum + s.position.v, 0);
  
  return {
    u: sumU / sockets.length,
    v: sumV / sockets.length,
  };
}

/**
 * Findet die gemeinsame System-ID für eine Gruppe von Blöcken
 */
function findCommonSystemId(blockIds: string[], blocks: PlacedBlock[], systems: BlockSystem[]): string | undefined {
  // Prüfe ob alle Blöcke zum gleichen System gehören
  const blockSystemIds = blockIds.map(blockId => {
    const block = blocks.find(b => b.id === blockId);
    return block?.systemId;
  });
  
  // Filtere undefined raus
  const definedSystemIds = blockSystemIds.filter(id => id !== undefined) as string[];
  
  if (definedSystemIds.length === 0) return undefined;
  
  // Prüfe ob alle gleich sind
  const firstSystemId = definedSystemIds[0];
  const allSame = definedSystemIds.every(id => id === firstSystemId);
  
  return allSame ? firstSystemId : undefined;
}

/**
 * Findet die Farbe für eine Verbindung basierend auf dem System
 */
function getConnectionColor(systemId: string | undefined, systems: BlockSystem[]): string | undefined {
  if (!systemId) return undefined;
  
  const system = systems.find(s => s.id === systemId);
  return system?.color;
}

/**
 * Berechnet den gemeinsamen DN-Wert
 */
function getCommonDn(sockets: SocketInfo[]): number | undefined {
  const dnValues = sockets.map(s => s.dn).filter(dn => dn !== undefined) as number[];
  
  if (dnValues.length === 0) return undefined;
  
  // Prüfe ob alle gleich sind
  const firstDn = dnValues[0];
  const allSame = dnValues.every(dn => dn === firstDn);
  
  return allSame ? firstDn : undefined;
}

/**
 * Berechnet den maximalen Durchmesser aus einer Socket-Gruppe
 */
function getMaxDiameter(sockets: SocketInfo[]): number | undefined {
  const diameters = sockets.map(s => s.diameter).filter(d => d !== undefined) as number[];
  
  if (diameters.length === 0) return undefined;
  
  return Math.max(...diameters);
}

/**
 * Erkennt automatisch alle Verbindungen zwischen Blöcken
 * Erstellt SocketConnection-Objekte für alle zusammenfallenden Sockets
 * WICHTIG: Behält bestehende Connections mit ihren Attributes bei!
 */
export function detectConnections(
  blocks: PlacedBlock[],
  systems: BlockSystem[],
  tolerance: number = 0.1,
  existingConnections: SocketConnection[] = []
): SocketConnection[] {
  // Sammle alle Sockets
  const allSockets = collectAllSockets(blocks);
  
  console.log('🔍 Verbindungserkennung:', allSockets.length, 'Sockets gefunden');
  
  // Gruppiere nach Position
  const socketGroups = groupSocketsByPosition(allSockets, tolerance);
  
  console.log('🔍 Verbindungserkennung:', socketGroups.length, 'Verbindungen erkannt');
  
  // Finde die höchste bestehende Verbindungsnummer
  let maxConnectionNumber = 0;
  existingConnections.forEach(conn => {
    const idAttr = conn.attributes?.find(attr => attr.name === 'V-ID');
    if (idAttr && typeof idAttr.value === 'number') {
      maxConnectionNumber = Math.max(maxConnectionNumber, idAttr.value);
    }
  });
  
  // Helper: Vergleicht zwei Socket-Arrays (unabhängig von Reihenfolge)
  const socketsMatch = (socketsA: SocketConnection['connectedSockets'], socketsB: SocketConnection['connectedSockets']): boolean => {
    // Guard gegen undefined/null
    if (!socketsA || !socketsB) return false;
    if (socketsA.length !== socketsB.length) return false;
    
    // Sortiere beide Arrays nach blockId für Vergleich
    const sortedA = [...socketsA].sort((a, b) => a.blockId.localeCompare(b.blockId));
    const sortedB = [...socketsB].sort((a, b) => a.blockId.localeCompare(b.blockId));
    
    return sortedA.every((socketA, i) => {
      const socketB = sortedB[i];
      return (
        socketA.blockId === socketB.blockId &&
        socketA.socketIndex === socketB.socketIndex &&
        socketA.lineIndex === socketB.lineIndex &&
        socketA.isStart === socketB.isStart
      );
    });
  };
  
  // Erstelle Connection-Objekte
  const connections: SocketConnection[] = socketGroups.map((group, index) => {
    const position = getAveragePosition(group);
    const blockIds = Array.from(new Set(group.map(s => s.blockId)));
    const systemId = findCommonSystemId(blockIds, blocks, systems);
    const color = getConnectionColor(systemId, systems);
    const dn = getCommonDn(group);
    const diameter = getMaxDiameter(group);
    
    // Dedupliziere Sockets: Pro Block nur EINEN Socket zählen
    const uniqueBlockSockets = new Map<string, typeof group[0]>();
    group.forEach(socket => {
      if (!uniqueBlockSockets.has(socket.blockId)) {
        uniqueBlockSockets.set(socket.blockId, socket);
      }
    });
    
    const connectedSockets = Array.from(uniqueBlockSockets.values()).map(socket => ({
      blockId: socket.blockId,
      socketIndex: socket.socketIndex,
      lineIndex: socket.lineIndex,
      isStart: socket.isStart,
    }));
    
    // KRITISCH: Prüfe ob diese Connection bereits existiert
    const existingConnection = existingConnections.find(existing => 
      socketsMatch(existing.connectedSockets, connectedSockets)
    );
    
    if (existingConnection) {
      // BESTEHENDE CONNECTION GEFUNDEN - Behalte sie mit allen Attribute!
      console.log(`  ♻️ Bestehende Connection wiederverwendet: ${existingConnection.id} (${existingConnection.attributes?.length || 0} attributes)`);
      
      // Update nur systemId und color (falls System sich geändert hat)
      return {
        ...existingConnection,
        systemId,
        color,
        dn,
        diameter,
        position, // Update position (könnte sich durch Block-Bewegung geändert haben)
      };
    } else {
      // NEUE CONNECTION - Erstelle mit V-ID Attribut
      const connectionNumber = maxConnectionNumber + index + 1;
      
      console.log(`  ✨ NEUE Connection erstellt: V-ID ${connectionNumber}`);
      
      return {
        id: generateId(),
        position,
        connectedSockets,
        systemId,
        color,
        dn,
        diameter,
        // Automatisches ID-Attribut
        attributes: [
          {
            id: `attr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: 'V-ID',
            type: 'integer' as const,
            value: connectionNumber,
            description: 'Automatisch generierte Verbindungsnummer',
            readonly: true,
          },
        ],
      };
    }
  });
  
  console.log('✅ Verbindungen erstellt:', connections.length);
  console.log(`  ♻️ ${connections.filter(c => existingConnections.some(e => e.id === c.id)).length} bestehende wiederverwendet`);
  console.log(`  ✨ ${connections.filter(c => !existingConnections.some(e => e.id === c.id)).length} neue erstellt`);
  
  connections.forEach((conn, i) => {
    console.log(`  Verbindung ${i + 1}:`, {
      id: conn.id,
      position: `(${conn.position.u.toFixed(2)}, ${conn.position.v.toFixed(2)})`,
      sockets: conn.connectedSockets.length,
      systemId: conn.systemId,
      dn: conn.dn,
      color: conn.color,
      attributesCount: conn.attributes?.length || 0,
    });
  });
  
  return connections;
}